import React, { useCallback, useEffect, useState } from "react"
import useMediaQuery from "@material-ui/core/useMediaQuery"
import { Snackbar } from "@material-ui/core"
import Alert from "@material-ui/lab/Alert"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import Button from "@material-ui/core/Button"
import Dialog from "@material-ui/core/Dialog"
import DialogActions from "@material-ui/core/DialogActions"
import DialogContent from "@material-ui/core/DialogContent"
import DialogTitle from "@material-ui/core/DialogTitle"
import Grid from "@material-ui/core/Grid"
import Link from "@material-ui/core/Link"
import TextField from "@material-ui/core/TextField"
import Typography from "@material-ui/core/Typography"
import Retry from "@material-ui/icons/Undo"
import FingerprintJS from "@fingerprintjs/fingerprintjs"
import { Ridecell } from "@ridecell/web-sdk"
import { injectIntl, FormattedMessage } from "react-intl"

import useGlobalContext from "../../utils/customHooks/useGlobalContext"
import LoadingButton from "../LoadingButton"
import messages from "./messages"
import { getErrorMessage } from "../../utils/common"

const useStyles = makeStyles((theme) => ({
  marginRight: {
    marginRight: "10px",
  },
  title: {
    marginLeft: "-25px",
  },
}))
const LoginDialog = (props) => {
  const {
    open,
    handleClose,
    handleLogin,
    history,
    intl: { formatMessage },
  } = props

  const classes = useStyles()
  const [error, setError] = useState()
  const [openSnackbar, setOpenSnackbar] = useState(false)
  const [nonce, setNonce] = useState(null)
  const [failedLogin, setFailedLogin] = useState(false)
  const [forgotPassword, setForgotPassword] = useState(false)
  const [forgotPasswordText, setForgotPasswordError] = useState(false)
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loginError, setLoginError] = useState(null)
  const [failedMfa, setFailedMfa] = useState(false)
  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")
  const [phone, setPhone] = useState("")
  const [mfaCode, setMfaCode] = useState("")
  const [resettingPassword, setResettingPassword] = useState(false)
  const [deviceId, setDeviceId] = useState("")

  useEffect(() => {
    ;(async () => {
      if (tenantSettings.mfaEnabled && tenantSettings.mfaEnabledForStaff) {
        const fp = await FingerprintJS.load()
        const result = await fp.get()
        setDeviceId(result.visitorId)
      }
    })()
  }, [])

  const toggleSnackbar = () => {
    setOpenSnackbar(!openSnackbar)
  }
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))
  const {
    state: { isAuthenticated, mfaDialog, tenantSettings },
  } = useGlobalContext()

  const handleChangeUsername = ({ target: { value } }) => setUsername(value)
  const handleChangePassword = ({ target: { value } }) => setPassword(value)
  const handleChangePhone = ({ target: { value } }) => setPhone(value)
  const handleMfaVerificationCode = ({ target: { value } }) => setMfaCode(value)
  const resetLogin = () => {
    setUsername("")
    setPassword("")
    setPhone("")
    setFailedLogin(false)
    setForgotPassword(false)
    setForgotPasswordError(null)
    setForgotPasswordSuccess(false)
  }
  const resetForgotPassword = () => {
    setPhone("")
    setForgotPasswordError(null)
    setForgotPassword(true)
  }
  const controlLogin = useCallback(() => {
    async function login() {
      try {
        setLoading(true)
        const response = await handleLogin({ username, password, deviceId })
        if (response["nonce"]) setNonce(response["nonce"])
        if (response && response["is_staff"]) {
          setFailedLogin(true)
          setLoginError(formatMessage(messages.staffNotAllowed))
        }
      } catch (err) {
        setFailedLogin(true)
        setLoginError(getErrorMessage(err))
      } finally {
        setLoading(false)
      }
    }
    login()
  }, [handleLogin, password, username])

  const controlMfaLogin = () => {
    Ridecell.mfaVerify({ code: mfaCode, nonce: nonce })
      .then(() => {
        controlLogin()
      })
      .catch((err) => {
        getErrorMessage(err)
        setError(getErrorMessage(err))
        setOpenSnackbar(true)
      })
  }

  const resendMfa = () => {
    Ridecell.mfaResend({ nonce: nonce })
      .then(() => {})
      .catch((err) => {
        getErrorMessage(err)
        setError(getErrorMessage(err))
        setOpenSnackbar(true)
      })
  }

  const openForgotPassword = (event) => {
    event.preventDefault()
    setForgotPassword(true)
  }

  const resendPassword = () => {
    setResettingPassword(true)
    Ridecell.resetPassword({ customerContact: phone })
      .then(([response]) => {
        setForgotPasswordSuccess(response)
        setResettingPassword(false)
      })
      .catch(({ message: [response] }) => {
        setForgotPasswordError(response)
        setResettingPassword(false)
      })
  }

  const resetWithEmail =
    tenantSettings?.passwordResetNotificationType === "email"
  // TO-DO this component is getting overly logic heavy, split into a few smaller ones
  return forgotPasswordSuccess ? (
    <Dialog
      className={classes.dialogBox}
      open={open}
      onClose={handleClose}
      onExited={resetLogin}
    >
      <DialogTitle>{formatMessage(messages.passwordResetLinkSent)}</DialogTitle>
      <DialogContent>
        {formatMessage(messages.successfullySent, {
          communication: resetWithEmail ? "email" : "sms",
        })}
      </DialogContent>
      <Button backgroundColor="#F8CC45" fullWidth onClick={resetLogin}>
        <Retry className={classes.marginRight} />
        <FormattedMessage {...messages.tryAgain} />
      </Button>
    </Dialog>
  ) : (
    <Dialog
      hideBackdrop
      className={classes.dialogBox}
      open={open}
      onClose={handleClose}
      onExited={resetLogin}
      PaperProps={
        isMobile
          ? {
              style: {
                width: "90%",
              },
            }
          : {
              style: {
                width: "40%",
                maxWidth: "50%",
              },
            }
      }
    >
      <>
        {forgotPassword && (
          <>
            <DialogTitle>{formatMessage(messages.forgotPassword)}</DialogTitle>
            {forgotPasswordText ? (
              <>
                <DialogContent>{forgotPasswordText}</DialogContent>
                <Button
                  backgroundColor="#F8CC45"
                  fullWidth
                  onClick={resetForgotPassword}
                >
                  <Retry className={classes.marginRight} />
                  <FormattedMessage {...messages.tryAgain} />
                </Button>
              </>
            ) : (
              <>
                <DialogContent>
                  {resetWithEmail ? (
                    <TextField
                      autoFocus
                      fullWidth
                      id="email"
                      label={formatMessage(messages.email)}
                      margin="dense"
                      onChange={handleChangePhone}
                      type="email"
                      value={phone}
                    />
                  ) : (
                    <TextField
                      autoFocus
                      fullWidth
                      id="phone"
                      label={formatMessage(messages.phoneNumber)}
                      margin="dense"
                      onChange={handleChangePhone}
                      type="tel"
                      value={phone}
                    />
                  )}
                </DialogContent>
                <DialogActions>
                  <LoadingButton
                    disabled={resettingPassword}
                    onClick={resendPassword}
                    text={formatMessage(messages.resetPasswordLink)}
                    loading={resettingPassword}
                    props={{ fullWidth: true }}
                  />
                </DialogActions>
              </>
            )}
          </>
        )}
        {mfaDialog && !isAuthenticated && (
          <>
            <DialogContent className={classes.title}>
              <DialogTitle>{formatMessage(messages.mfaTitle)}</DialogTitle>
            </DialogContent>
            <DialogContent>
              <Grid
                container
                direction="row"
                justify="center"
                alignItems="center"
              ></Grid>
              <TextField
                autoFocus
                fullWidth
                id="code"
                label={formatMessage(messages.verificationCode)}
                margin="dense"
                onChange={handleMfaVerificationCode}
                value={mfaCode}
              />
            </DialogContent>
            <DialogActions className={classes.customDialogActions}>
              <Grid
                container
                direction="column"
                justify="center"
                alignItems="center"
              >
                <LoadingButton
                  disabled={!mfaCode}
                  onClick={controlMfaLogin}
                  text={formatMessage(messages.submit)}
                  loading={loading}
                  props={{
                    fullWidth: true,
                    variant: "contained",
                    style: {
                      marginTop: "12px",
                      padding: "12px",
                      fontSize: "15px",
                    },
                  }}
                />
                <Button
                  backgroundColor="#FFF"
                  variant="contained"
                  style={{
                    width: "95%",
                    marginTop: "12px",
                    marginBottom: "15px",
                    padding: "12px",
                  }}
                  onClick={resendMfa}
                >
                  {formatMessage(messages.resendCode)}
                </Button>
              </Grid>
            </DialogActions>
            {failedMfa && (
              <DialogContent>
                <DialogTitle>{failedMfa}</DialogTitle>
              </DialogContent>
            )}
          </>
        )}
        {!failedLogin && !forgotPassword && !isAuthenticated && !mfaDialog && (
          <>
            <DialogContent>
              <Grid
                container
                direction="row"
                justify="center"
                alignItems="center"
              ></Grid>
              <TextField
                autoFocus
                fullWidth
                id="username"
                label={formatMessage(messages.email)}
                margin="dense"
                onChange={handleChangeUsername}
                type="email"
                value={username}
              />
              <TextField
                id="password"
                fullWidth
                label={formatMessage(messages.password)}
                margin="dense"
                onChange={handleChangePassword}
                type="password"
                value={password}
              />
            </DialogContent>
            <DialogActions className={classes.customDialogActions}>
              <Grid
                container
                direction="column"
                justify="center"
                alignItems="center"
              >
                <LoadingButton
                  onClick={controlLogin}
                  text={formatMessage(messages.login)}
                  loading={loading}
                  props={{
                    fullWidth: true,
                    variant: "contained",
                    style: {
                      marginTop: "12px",
                      padding: "12px",
                      fontSize: "15px",
                    },
                  }}
                />
                {!tenantSettings.skipAppRegistrationScreen && (
                  <Button
                    backgroundColor="#FFF"
                    variant="contained"
                    style={{
                      width: "95%",
                      marginTop: "12px",
                      marginBottom: "15px",
                      padding: "12px",
                    }}
                    onClick={() => history.push("/signup")}
                  >
                    {formatMessage(messages.createAccount)}
                  </Button>
                )}
                <Typography>
                  <Link href="#" onClick={openForgotPassword}>
                    {formatMessage(messages.forgotPassword)}
                  </Link>
                </Typography>
              </Grid>
            </DialogActions>
          </>
        )}
        {failedLogin && !forgotPassword && (
          <DialogContent>
            <DialogTitle>
              {loginError || formatMessage(messages.loginFailed)}
            </DialogTitle>
            <Button backgroundColor="#F8CC45" fullWidth onClick={resetLogin}>
              <Retry className={classes.marginRight} />
              <FormattedMessage {...messages.tryAgain} />
            </Button>
          </DialogContent>
        )}
        <Snackbar
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          autoHideDuration={5000}
          onClose={toggleSnackbar}
          open={openSnackbar}
        >
          <Alert onClose={toggleSnackbar} severity={"error"} variant="filled">
            {error}
          </Alert>
        </Snackbar>
      </>
    </Dialog>
  )
}
export default injectIntl(LoginDialog)
